import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../common/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Smartphone,
  Mail,
  Lock,
  ShieldCheck,
  Image,
  X,
  KeyRound,
  UserCog,
  Trash2,
} from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { API_URL } from '../../api/config';
import PushNotificationToggle from '../../common/components/PushNotificationToggle';
import DeletePinModal from './settingModal/DeletePinModal';

const SettingsPage = () => {
  const { userToken: token, language, updateUserLanguage } = useAuth();
  const navigate = useNavigate();

  const handleLanguageChange = async (lang) => {
    const success = await updateUserLanguage(lang);
    if (success) {
      toast.success('Language settings updated successfully');
    } else {
      toast.error('Failed to update language settings');
    }
  };

  const [settings, setSettings] = useState({
    restaurantName: '',
    phoneNumber: '',
    gstin: '',
    fssaiNumber: '',
    businessEmail: '',
    location: '',
    outletAddress: '',
    logoUrl: '',
    note: 'Thank you Visit Again',
  });

  const [errors, setErrors]         = useState({});
  const [isSaving, setIsSaving]     = useState(false);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoPreview, setLogoPreview]   = useState('');

  // PIN protection state (outlet-level toggle)
  const [pinProtection, setPinProtection] = useState(false);

  // Admin / Manager PIN status
  const [pinSettings, setPinSettings] = useState({
    hasAdminPin:               false,
    adminPinOwnedByThisOutlet: false, // false = set on a different outlet, locked here
    adminEmail:                '',
    adminPinOwnerOutletId:     null,
    hasManagerPin:             false,
    managerEmail:              '',
  });

  // Delete PIN modal
  const [deleteModal, setDeleteModal] = useState(null); // null | 'admin' | 'manager'

  // ── Fetch outlet settings ──────────────────────────────
  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.data) {
          setSettings(data.data);
          if (data.data.logoUrl) setLogoPreview(data.data.logoUrl);
        }
      }
    } catch (e) {
      console.error('Error fetching settings:', e);
    }
  }, [token]);

  // ── Fetch PIN settings ────────────────────────────────
  const fetchPinSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/pin-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.data) {
          setPinProtection(data.data.pinProtectionEnabled || false);
          setPinSettings({
            hasAdminPin:               data.data.hasAdminPin               || false,
            adminPinOwnedByThisOutlet: data.data.adminPinOwnedByThisOutlet ?? false,
            adminEmail:                data.data.adminEmail                 || '',
            adminPinOwnerOutletId:     data.data.adminPinOwnerOutletId      || null,
            hasManagerPin:             data.data.hasManagerPin              || false,
            managerEmail:              data.data.managerEmail               || '',
          });
        }
      }
    } catch (e) {
      console.error('Error fetching PIN settings:', e);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
    fetchPinSettings();
  }, [fetchSettings, fetchPinSettings]);

  // ── Form validation ───────────────────────────────────
  const validateForm = () => {
    const newErrors = {};
    if (!settings.restaurantName?.trim())
      newErrors.restaurantName = 'Restaurant name is required';
    if (!settings.phoneNumber?.trim())
      newErrors.phoneNumber = 'Phone number is required';
    else if (!/^\d{10}$/.test(settings.phoneNumber.trim()))
      newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
    if (settings.businessEmail?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(settings.businessEmail.trim()))
      newErrors.businessEmail = 'Please enter a valid email address';
    if (settings.gstin?.trim() && !/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/.test(settings.gstin.trim()))
      newErrors.gstin = 'Please enter a valid GSTIN';
    if (settings.fssaiNumber?.trim() && !/^\d{14}$/.test(settings.fssaiNumber.trim()))
      newErrors.fssaiNumber = 'Please enter a valid 14-digit FSSAI number';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Logo ──────────────────────────────────────────────
  const pickImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, logo: 'File size should be less than 5MB' }));
        return;
      }
      setSelectedLogo(file);
      setLogoPreview(URL.createObjectURL(file));
      setErrors(prev => ({ ...prev, logo: '', submit: '' }));
    };
    input.click();
  };

  const handleRemoveLogo = useCallback(() => {
    setLogoPreview('');
    setSelectedLogo(null);
    setSettings(prev => ({ ...prev, logoUrl: '' }));
    setErrors(prev => ({ ...prev, logo: '', submit: '' }));
  }, []);

  // ── Save settings ─────────────────────────────────────
  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    setErrors(prev => ({ ...prev, submit: '' }));

    const formData = new FormData();
    formData.append('restaurantName', settings.restaurantName.trim());
    formData.append('phoneNumber',    settings.phoneNumber.trim());
    formData.append('gstin',          settings.gstin?.trim()        || '');
    formData.append('fssaiNumber',    settings.fssaiNumber?.trim()  || '');
    formData.append('businessEmail',  settings.businessEmail?.trim()|| '');
    formData.append('location',       settings.location?.trim()     || '');
    formData.append('outletAddress',  settings.outletAddress?.trim()|| '');
    formData.append('note',           settings.note?.trim()         || 'Thank you Visit Again');
    if (selectedLogo) formData.append('logo', selectedLogo);

    try {
      const res = await fetch(`${API_URL}/settings`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        setSettings(data.data);
        toast.success('Settings saved successfully!');
      } else {
        setErrors(prev => ({ ...prev, submit: data.message || 'Failed to save settings' }));
        toast.error(data.message || 'Failed to save settings');
      }
    } catch {
      toast.error('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (name, value) => {
    setSettings(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '', submit: '' }));
  };

  // ── Shared input class ────────────────────────────────
  const inputCls = (errKey) =>
    `w-full text-base font-medium text-slate-900 bg-transparent border-none outline-none ${errors[errKey] ? 'text-red-500' : ''}`;

  return (
    <div className="min-h-full bg-[#F8FAFC] p-8 pb-32">
      <ToastContainer position="top-right" autoClose={5000} />

      <div className="max-w-6xl mx-auto space-y-10">

        {/* Page header */}
        <div className="space-y-1">
          <h1 className="text-[32px] font-bold text-slate-900 tracking-tight">General Settings</h1>
          <p className="text-sm font-medium text-slate-400">
            Manage your outlet details, billing preferences, and security access.
          </p>
        </div>

        {/* ── BUSINESS PROFILE ─────────────────────────── */}
        <section className="space-y-4">
          <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Business Profile</h3>
          <div className="bg-white rounded-[1.5rem] border border-slate-100 shadow-sm overflow-hidden">

            {/* Logo */}
            <div className="p-6 border-b border-slate-50">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">
                Restaurant Logo
              </label>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={pickImage}
                  className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-slate-200 rounded-xl hover:border-slate-300 transition-colors"
                >
                  <Image className="text-slate-400" size={18} />
                  <span className="text-sm font-medium text-slate-600">
                    {logoPreview ? 'Change Logo' : 'Upload Logo'}
                  </span>
                </button>
                {logoPreview && (
                  <div className="relative">
                    <img src={logoPreview} alt="Logo" className="w-20 h-20 rounded-xl object-cover border border-slate-100" />
                    <button
                      onClick={handleRemoveLogo}
                      className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 hover:bg-red-600 transition-colors shadow-sm"
                    >
                      <X className="text-white" size={14} />
                    </button>
                  </div>
                )}
              </div>
              {errors.logo && <p className="text-xs text-red-500 mt-2">{errors.logo}</p>}
            </div>

            {/* Name + GST */}
            <div className="grid grid-cols-1 md:grid-cols-2 border-b border-slate-50">
              <div className="p-6 space-y-1 border-r border-slate-50">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Outlet Name</label>
                <input
                  type="text"
                  className={inputCls('restaurantName')}
                  value={settings.restaurantName}
                  onChange={e => handleChange('restaurantName', e.target.value)}
                  placeholder="Enter outlet name"
                />
                {errors.restaurantName && <p className="text-xs text-red-500">{errors.restaurantName}</p>}
              </div>
              <div className="p-6 space-y-1">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">GST Number</label>
                <input
                  type="text"
                  className={`${inputCls('gstin')} uppercase`}
                  value={settings.gstin}
                  onChange={e => handleChange('gstin', e.target.value)}
                  placeholder="29GGGGG1314R9Z6"
                />
                {errors.gstin && <p className="text-xs text-red-500">{errors.gstin}</p>}
              </div>
            </div>

            {/* FSSAI */}
            <div className="p-6 space-y-1 border-b border-slate-50">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">FSSAI Number</label>
              <input
                type="text"
                className={inputCls('fssaiNumber')}
                value={settings.fssaiNumber}
                onChange={e => handleChange('fssaiNumber', e.target.value)}
                placeholder="11223344556677"
                maxLength={14}
              />
              {errors.fssaiNumber && <p className="text-xs text-red-500">{errors.fssaiNumber}</p>}
            </div>

            {/* Address + Location */}
            <div className="grid grid-cols-1 md:grid-cols-3">
              <div className="md:col-span-2 p-6 space-y-1 border-r border-slate-50">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Outlet Address</label>
                <input
                  type="text"
                  className={inputCls()}
                  value={settings.outletAddress}
                  onChange={e => handleChange('outletAddress', e.target.value)}
                  placeholder="Enter outlet address"
                />
              </div>
              <div className="p-6 space-y-1">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Location</label>
                <input
                  type="text"
                  className={inputCls()}
                  value={settings.location}
                  onChange={e => handleChange('location', e.target.value)}
                  placeholder="City/Area"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── CONTACT INFORMATION ───────────────────────── */}
        <section className="space-y-4">
          <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Contact Information</h3>
          <div className="bg-white rounded-[1.5rem] border border-slate-100 shadow-sm overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 border-b border-slate-50">
              <div className="p-6 flex items-center gap-4 border-r border-slate-50">
                <Smartphone size={16} className="text-slate-400 shrink-0" />
                <div className="space-y-1 flex-1">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Phone Number</label>
                  <input
                    type="tel"
                    className={inputCls('phoneNumber')}
                    value={settings.phoneNumber}
                    onChange={e => handleChange('phoneNumber', e.target.value)}
                    placeholder="+91 98765 43210"
                  />
                  {errors.phoneNumber && <p className="text-xs text-red-500">{errors.phoneNumber}</p>}
                </div>
              </div>
              <div className="p-6 flex items-center gap-4">
                <Mail size={16} className="text-slate-400 shrink-0" />
                <div className="space-y-1 flex-1">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Email Address</label>
                  <input
                    type="email"
                    className={`${inputCls('businessEmail')} lowercase`}
                    value={settings.businessEmail}
                    onChange={e => handleChange('businessEmail', e.target.value)}
                    placeholder="manager@outlet.com"
                  />
                  {errors.businessEmail && <p className="text-xs text-red-500">{errors.businessEmail}</p>}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── LANGUAGE SETTINGS ─────────────────────────── */}
        <section className="space-y-4">
          <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Language Settings</h3>
          <div className="bg-white rounded-[1.5rem] border border-slate-100 shadow-sm p-6">
            <div className="max-w-xs space-y-2">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">
                Preferred Language
              </label>
              <select
                value={language}
                onChange={e => handleLanguageChange(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 outline-none focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all cursor-pointer"
              >
                <option value="en">English</option>
                <option value="hi">Hindi (हिंदी)</option>
                <option value="pa">Punjabi (ਪੰਜਾਬੀ)</option>
                <option value="bn">Bengali (বাংলা)</option>
                <option value="mr">Marathi (मराठी)</option>
                <option value="gu">Gujarati (ગુજરાતી)</option>
                <option value="ta">Tamil (தமிழ்)</option>
                <option value="te">Telugu (తెలుగు)</option>
                <option value="kn">Kannada (ಕನ್ನಡ)</option>
                <option value="ml">Malayalam (മലയാളം)</option>
                <option value="or">Odia (ଓଡ଼ିଆ)</option>
                <option value="as">Assamese (অসমীয়া)</option>
                <option value="ur">Urdu (اردو)</option>
              </select>
            </div>
          </div>
        </section>

        {/* ── BILL CUSTOMIZATION + NOTIFICATIONS ───────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <section className="space-y-4 flex flex-col">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Bill Customization</h3>
            <div className="bg-white rounded-[1.5rem] border border-slate-100 shadow-sm p-6 space-y-4 flex flex-col flex-1">
              <div className="space-y-2 flex flex-col flex-1">
                <label className="block text-[11px] font-bold text-slate-800">Message on Bill Footer</label>
                <textarea
                  className="w-full bg-slate-50/50 border border-slate-100 rounded-xl p-4 flex-1 text-sm font-medium text-slate-600 leading-relaxed resize-none outline-none focus:border-blue-300"
                  value={settings.note}
                  onChange={e => handleChange('note', e.target.value)}
                  placeholder="Thank you for dining with us!"
                />
                <div className="flex justify-end">
                  <span className="text-[10px] font-bold text-slate-300 tracking-widest uppercase">
                    {settings.note?.length || 0}/150 characters
                  </span>
                </div>
              </div>
            </div>
          </section>

          <PushNotificationToggle token={token} />
        </div>

        {/* ── SECURITY & ACCESS CONTROL ─────────────────── */}
        <section className="space-y-4">
          <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
            Security & Access Control
          </h3>
          <div className="bg-white rounded-[1.5rem] border border-slate-100 shadow-sm overflow-hidden divide-y divide-slate-50">

            {/* Outlet PIN protection (navigate to full PIN protection page) */}
            <div
              className="p-6 flex items-center justify-between hover:bg-slate-50/50 transition-colors cursor-pointer"
              onClick={() => navigate('/pin-protection')}
            >
              <div className="flex items-center gap-4">
                <Lock size={16} className="text-slate-400 shrink-0" />
                <div>
                  <h4 className="text-sm font-bold text-slate-900">Outlet PIN Protection</h4>
                  <p className="text-xs font-medium text-slate-400">
                    Require PIN for critical actions and manage role-based access.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={e => { e.stopPropagation(); navigate('/pin-protection'); }}
                  className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none ${pinProtection ? 'bg-blue-600' : 'bg-slate-200'}`}
                >
                  <div className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${pinProtection ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
                <span className="text-slate-300">›</span>
              </div>
            </div>

            {/* ── ADMIN PIN ROW ── */}
            <div className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <ShieldCheck size={16} className="text-purple-500 shrink-0" />
                <div>
                  <h4 className="text-sm font-bold text-slate-900">Admin PIN</h4>
                  <p className="text-xs font-medium text-slate-400">
                    {!pinSettings.hasAdminPin && 'Not set — one PIN shared across all your outlets'}
                    {pinSettings.hasAdminPin && pinSettings.adminPinOwnedByThisOutlet && (
                      <>Set on this outlet · email: <span className="text-slate-600">{pinSettings.adminEmail || '—'}</span></>
                    )}
                    {pinSettings.hasAdminPin && !pinSettings.adminPinOwnedByThisOutlet && (
                      <span className="text-amber-600 font-semibold">
                        Set on another outlet — can only be changed from that outlet
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Only show Set/Change/Delete if this outlet owns the admin PIN, or no PIN set yet */}
                {!pinSettings.hasAdminPin && (
                  <button
                    onClick={() => navigate('/setup-admin-pin')}
                    className="px-3 py-1.5 text-xs font-bold text-purple-600 border border-purple-200 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                  >
                    Set PIN
                  </button>
                )}
                {pinSettings.hasAdminPin && pinSettings.adminPinOwnedByThisOutlet && (
                  <>
                    <button
                      onClick={() => navigate('/setup-admin-pin')}
                      className="px-3 py-1.5 text-xs font-bold text-purple-600 border border-purple-200 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                    >
                      Change
                    </button>
                    <button
                      onClick={() => setDeleteModal('admin')}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-red-600 border border-red-200 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <Trash2 size={13} />
                      Delete
                    </button>
                  </>
                )}
                {pinSettings.hasAdminPin && !pinSettings.adminPinOwnedByThisOutlet && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-400 border border-slate-200 bg-slate-50 rounded-lg cursor-not-allowed select-none">
                    <Lock size={12} />
                    Locked
                  </div>
                )}
              </div>
            </div>

            {/* ── MANAGER PIN ROW ── */}
            <div className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <UserCog size={16} className="text-blue-500 shrink-0" />
                <div>
                  <h4 className="text-sm font-bold text-slate-900">Manager PIN</h4>
                  <p className="text-xs font-medium text-slate-400">
                    {pinSettings.hasManagerPin
                      ? <>Set · stored email: <span className="text-slate-600">{pinSettings.managerEmail || '—'}</span></>
                      : 'Not set — per-outlet PIN with limited module access'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/setup-manager-pin')}
                  className="px-3 py-1.5 text-xs font-bold text-blue-600 border border-blue-200 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  {pinSettings.hasManagerPin ? 'Change' : 'Set PIN'}
                </button>
                {pinSettings.hasManagerPin && (
                  <button
                    onClick={() => setDeleteModal('manager')}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-red-600 border border-red-200 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <Trash2 size={13} />
                    Delete
                  </button>
                )}
              </div>
            </div>

            {/* ── OUTLET PIN ROW ── */}
            <div className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <KeyRound size={16} className="text-green-500 shrink-0" />
                <div>
                  <h4 className="text-sm font-bold text-slate-900">Outlet PIN</h4>
                  <p className="text-xs font-medium text-slate-400">
                    Quick 4-digit PIN for this outlet's daily login
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/pin-protection')}
                className="px-3 py-1.5 text-xs font-bold text-green-700 border border-green-200 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
              >
                Manage
              </button>
            </div>

            {/* ── DELETE ACCOUNT ── */}
            <div className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <ShieldCheck size={16} className="text-slate-400 shrink-0" />
                <div>
                  <h4 className="text-sm font-bold text-slate-900">Account Management</h4>
                  <p className="text-xs font-medium text-slate-400">Delete your account and all associated data.</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/account-delete')}
                className="text-[10px] font-black text-red-600 uppercase tracking-widest hover:text-red-700 transition-colors"
              >
                Delete Account
              </button>
            </div>
          </div>
        </section>

      </div>

      {/* ── Floating Save Bar ─────────────────────────────── */}
      <div className="fixed bottom-0 left-0 lg:left-60 right-0 bg-white/80 backdrop-blur-xl border-t border-slate-100 p-4 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <p className="text-sm font-medium text-slate-400">
            {isSaving ? 'Saving changes...' : 'Make changes and save'}
          </p>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchSettings}
              className="px-8 py-2.5 rounded-xl text-sm font-bold text-slate-600 border border-slate-200 hover:bg-slate-50 transition-all"
            >
              Reset
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSaving}
              className={`px-10 py-2.5 rounded-xl font-bold text-sm shadow-xl shadow-blue-100 transition-all transform active:scale-95 ${
                isSaving ? 'bg-blue-400 text-white cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Delete PIN Modals ────────────────────────────── */}
      {deleteModal === 'admin' && (
        <DeletePinModal
          role="admin"
          token={token}
          onSuccess={() => {
            setPinSettings(prev => ({ ...prev, hasAdminPin: false, adminEmail: '', adminPinOwnedByThisOutlet: false, adminPinOwnerOutletId: null }));
            fetchPinSettings();
          }}
          onClose={() => setDeleteModal(null)}
        />
      )}
      {deleteModal === 'manager' && (
        <DeletePinModal
          role="manager"
          token={token}
          onSuccess={() => {
            setPinSettings(prev => ({ ...prev, hasManagerPin: false, managerEmail: '' }));
            fetchPinSettings();
          }}
          onClose={() => setDeleteModal(null)}
        />
      )}
    </div>
  );
};

export default SettingsPage;